<template>
    <b-modal
        no-fade
        ref="dialog"
        title-tag="h2"
        :title="title"
        :lazy="true"
        id="modal-item-dialog"
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-form-group label-for="item" label-cols="3" label="Item:">
            <!-- TODO: add "New Item" option to allow user to add new item -->
            <vet-select-item-integrated
                v-if="sourceCatalog"
                :sourceCatalog="sourceCatalog"
                placeholder="Item (required)"
                v-model="item"
                name="item"
                v-validate="'required'"
                :class="{ 'border-danger': errors.has('item') }"
            />
            <vet-select 
                v-else           
                module="item-po"
                :sourceCatalog="sourceCatalog"
                placeholder="Item (required)"
                v-model="item"
                name="item"
                v-validate="'required'"
                :class="{ 'border-danger': errors.has('item') }"
            />
        </b-form-group>
        <b-form-group label-for="quantity" label-cols="3" label="Quantity:">
            <div class="quantity">
                <b-input
                    v-model="quantity"
                    :class="{
                        'border-danger': errors.has('quantity')
                    }"
                    v-validate="'numericalWithDecimal|required|decimal:5'"
                    name="quantity"
                    type="text"
                    placeholder="Quantity (required)"
                    :disabled="isSaving"
                />
                <span>{{ uom }}</span>
            </div>
        </b-form-group>
        <b-form-group
            v-if="module === constant.ORDER && isIntegrated"
            label-for="unitCost"
            label-cols="3"
            label="Unit Cost:"
        >
            <b-input
                v-model="unitCost"
                name="unitCost"
                type="text"
                placeholder="Cost"
                :disabled="isSaving || isIntegrated"
            />
        </b-form-group>
        <b-form-group
            v-if="module === constant.ORDER"
            label-for="totalCost"
            label-cols="3"
            label="Total Cost:"
        >
            <b-input
                v-model="totalCost"
                name="totalCost"
                type="text"
                placeholder="Cost"
                :disabled="isSaving || isIntegrated"
            />
        </b-form-group>
        <b-form-group label-for="notes" label-cols="3" label="Notes:">
            <b-textarea
                v-model="notes"
                name="notes"
                placeholder="Notes (optional)"
                :disabled="isSaving"
            />
        </b-form-group>

        <template v-slot:modal-footer v-if="isLoadingPriceExternal || isSaving">
            <loader :margin="1" />
        </template>
        <template v-slot:modal-footer="{ cancel }" v-else>
            <b-button
                class="mr-auto"
                variant="secondary"
                @click="cancel"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <!-- save button for ADD mode -->
            <b-button-group v-if="isEnabledButtonSaveGroup">
                <b-dropdown
                    v-if="!continues"
                    @click="save(false)"
                    left
                    split
                    text="Save + Done"
                    variant="primary"
                    :disabled="!isItemAvailable"
                >
                    <b-dropdown-item @click="save(true)">
                        Save + Add New
                    </b-dropdown-item>
                </b-dropdown>
                <b-dropdown
                    v-else
                    @click="save(true)"
                    left
                    split
                    text="Save + Add New"
                    variant="primary"
                    :disabled="!isItemAvailable"
                >
                    <b-dropdown-item @click="save(false)">
                        Save + Done
                    </b-dropdown-item>
                </b-dropdown>
            </b-button-group>

            <!-- save button for EDIT mode -->
            <b-button
                variant="primary"
                @click="save(false)"
                v-if="isEnabledButtonSaveSingle"
            >
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import Loader from "@/module/commons/loader";
import VetSelect from "@/module/commons/vet-select";
import VetSelectItemIntegrated from "@/components/commons/vet-select-item-integrated";

const ADD = "ADD";
const EDIT = "EDIT";
const TEMPLATE = "TEMPLATE";
const ORDER = "ORDER"; // <-- purchase order

export default {
    components: {
        Loader,
        VetSelect,
        VetSelectItemIntegrated
    },
    data() {
        return {
            constant: {
                TEMPLATE: TEMPLATE,
                ORDER: ORDER
            },
            mode: ADD,
            module: null, // TEMPLATE | ORDER
            title: "New Item",
            item: null,
            isSaving: false,
            quantity: null,
            unitCost: 0,
            totalCost: 0,
            notes: "",
            sourceCatalog: null,
            continues: false,
            elementId: null,
            uom: "Unit(s)",
            isLoadingPriceExternal: false,
            isItemAvailable: true
        };
    },
    computed: {
        isIntegrated() {
            return this.sourceCatalog && this.sourceCatalog !== "";
        },
        isEnabledButtonSaveGroup() {
            if (this.mode === EDIT) {
                return false;
            }

            if (this.isSaving) {
                return false;
            }

            return true;
        },
        isEnabledButtonSaveSingle() {
            if (this.mode === ADD) {
                return false;
            }

            if (this.isSaving) {
                return false;
            }

            return true;
        }
    },
    watch: {
        item(val) {
            if (val && val.container_type_label) {
                this.uom = val.container_type_label;
            } else {
                this.uom = "";
            }

            if (val && this.isIntegrated && this.module === ORDER) {
                let productId = val.item_id || val.product_id;

                if (productId) {
                    this.fetchItemPrice(productId);
                }
            }
        },
        quantity(value) {
            this.updateTotalCost(value, this.unitCost);
        },
        unitCost(value) {
            if (this.module === ORDER && this.isIntegrated) {
                this.updateTotalCost(this.quantity, value);
            }
        }
    },
    methods: {
        open(params = {}) {
            let {
                mode,
                module,
                templateId,
                orderId,
                elementId,
                itemId,
                note,
                quantity,
                itemName,
                uomLabel,
                sourceCatalog,
                totalCost
            } = params;

            this.module = module;
            this.sourceCatalog = sourceCatalog;
            this.mode = mode;

            if (mode === ADD) {
                this.title = "New Item";
            } else if (mode === EDIT) {
                this.title = "Edit Item";
            }

            if (module === TEMPLATE) {
                this.templateId = templateId;
                this.orderId = null;

                if (mode === EDIT) {
                    this.notes = note;
                    this.elementId = elementId;
                    this.quantity = quantity;
                    this.item = {
                        label: itemName,
                        item_id: itemId,
                        container_type_label: uomLabel
                    };
                }
            } else if (module === ORDER) {
                this.orderId = orderId;
                this.templateId = null;

                if (mode === EDIT) {
                    this.notes = note;
                    this.elementId = elementId;
                    this.quantity = quantity;
                    this.totalCost = totalCost;
                    this.unitCost = totalCost / quantity;
                    this.item = {
                        label: itemName,
                        item_id: itemId,
                        container_type_label: uomLabel
                    };
                }
            }

            this.$refs.dialog.show();
        },
        updateTotalCost(qty, price) {
            let properQty = Number(qty);
            let properPrice = Number(price);

            if (isNaN(properQty)) {
                properQty = 0;
            }

            if (isNaN(properPrice)) {
                properPrice = 0;
            }

            let total = properPrice * properQty;
            this.totalCost = Number(total.toFixed(2));
        },
        async save(repeat) {
            try {
                let valid = await this.$validator.validateAll();
                let itemId = null;

                let isIntegratedSupplier =
                    this.sourceCatalog && this.sourceCatalog !== "";

                if (!valid) {
                    return false;
                }

                let validQuantity = Number(this.quantity);
                if (isNaN(validQuantity)) {
                    throw new Error("Invalid quantity");
                }

                this.isSaving = true;

                // for integrated supplier:
                if (isIntegratedSupplier) {
                    if (this.mode === ADD) {
                        itemId = this.item.product_id;
                    } else if (this.mode === EDIT) {
                        itemId = this.item.item_id;
                    }

                    // Module ORDER already checked the price, so don't need this when saving
                    if (this.module === TEMPLATE) {
                        await this.checkItemExternal(itemId);
                    }
                }
                // default
                else {
                    itemId = this.item.item_id;
                }

                // Template module, ADD
                if (this.module === TEMPLATE && this.mode === ADD) {
                    let res = await this.$http.post(
                        `${process.env.BARRAMUNDI}account/purchase-order-element`,
                        {
                            template_id: this.templateId,
                            item_id: itemId,
                            quantity: validQuantity,
                            note: this.notes || ""
                        }
                    );

                    let response = res.data.response;

                    // backend validator error
                    if (
                        response &&
                        response.message &&
                        response.message.errors &&
                        response.message.errors[400]
                    ) {
                        throw new Error("Unable to save. Validation error.");
                    }

                    this._message(res.data.response.message); // Record saved
                    this.$emit("reload");

                    if (repeat) {
                        this.item = null;
                        this.quantity = null;
                        this.notes = "";
                        this.isSaving = false;
                        this.continues = true; // change mode to 'Add + New'
                        this.$validator.reset();
                    } else {
                        this.$refs.dialog.hide();
                    }
                }
                // Template module, EDIT
                else if (this.module === TEMPLATE && this.mode === EDIT) {
                    let res = await this.$http.put(
                        `${process.env.BARRAMUNDI}account/purchase-order-element/${this.elementId}`,
                        {
                            item_id: itemId,
                            quantity: this.quantity,
                            note: this.notes
                        }
                    );

                    this._message(res.data.response.message); // Record saved
                    this.$emit("reload");
                    this.$refs.dialog.hide();
                }
                // Order module, EDIT
                else if (this.module === ORDER && this.mode === EDIT) {
                    if (isIntegratedSupplier) {
                        await this.$http.post(
                            `${process.env.BARRAMUNDI}inventory/order/item/save`,
                            [
                                {
                                    purchase_order_id: this.orderId,
                                    purchase_order_item_id: this.elementId,
                                    source_catalog: this.sourceCatalog,
                                    source_product_id: itemId,
                                    item_name: this.item.label,
                                    quantity_ordered: validQuantity,
                                    price: this.unitCost,
                                    line_total: this.totalCost,
                                    notes: this.notes
                                }
                            ]
                        );
                    } else {
                        let validTotalCost = Number(this.totalCost);

                        if (isNaN(validTotalCost)) {
                            throw new Error("Invalid total cost value!");
                        }

                        let price = validTotalCost / validQuantity;

                        await this.$http.post(
                            `${process.env.BARRAMUNDI}inventory/order/item/save`,
                            [
                                {
                                    purchase_order_id: this.orderId,
                                    purchase_order_item_id: this.elementId,
                                    item_id: itemId,
                                    item_name: this.item.label,
                                    quantity_ordered: validQuantity,
                                    price: price,
                                    line_total: validTotalCost,
                                    notes: this.notes
                                }
                            ]
                        );
                    }

                    this._message("Item updated");
                    this.$emit("reload");
                    this.$refs.dialog.hide();
                }
                // Order module, ADD
                else if (this.module === ORDER && this.mode === ADD) {
                    let validTotalCost = Number(this.totalCost);

                    if (isNaN(validTotalCost)) {
                        throw new Error("Invalid total cost value!");
                    }

                    let price;
                    let data = {};

                    if (isIntegratedSupplier) {
                        data = [
                            {
                                source_catalog: this.sourceCatalog,
                                purchase_order_id: this.orderId,
                                source_product_id: itemId,
                                item_name: this.item.display_name,
                                quantity_ordered: validQuantity,
                                price: this.unitCost,
                                line_total: validTotalCost,
                                notes: this.notes
                            }
                        ];
                    } else {
                        price = validTotalCost / validQuantity;

                        data = [
                            {
                                purchase_order_id: this.orderId,
                                item_id: itemId,
                                item_name: this.item.label,
                                quantity_ordered: validQuantity,
                                price: price,
                                line_total: validTotalCost,
                                notes: this.notes
                            }
                        ];
                    }

                    await this.$http.post(
                        `${process.env.BARRAMUNDI}inventory/order/item/save`,
                        data
                    );

                    this._message("Item added");
                    this.$emit("reload");

                    if (repeat) {
                        this.item = null;
                        this.quantity = null;
                        this.notes = "";
                        this.isSaving = false;
                        this.continues = true; // change mode to 'Add + New'
                        this.$validator.reset();
                        this.totalCost = 0;
                    } else {
                        this.$refs.dialog.hide();
                    }
                }
                // others
                else {
                    throw new Error("Invalid module identifier.");
                }
            } catch (error) {
                this.isSaving = false;
                this._message(error.message, "danger");
            }
        },
        async checkItemExternal(productId) {
            let product = await this.$http.post(
                `${process.env.BARRAMUNDI}inventory/order/catalog/status`,
                {
                    source_catalog: this.sourceCatalog,
                    source_product_id: [productId] // must be in array
                }
            );

            if (
                product.data &&
                product.data.response &&
                product.data.response.resources &&
                product.data.response.resources[0]
            ) {
                let result = product.data.response.resources[0];
                let errorMessage = "Item is not available";

                if (result.halt === true) {
                    throw new Error(errorMessage);
                }

                if (result.warning_message === "Bad Request") {
                    throw new Error(errorMessage);
                }

                if (result.warning_message && result.warning_message !== "") {
                    throw new Error(result.warning_message);
                }

                if (this.module === ORDER) {
                    return result;
                } else {
                    return null;
                }
            } else {
                throw new Error("Unable to check availability for this item.");
            }
        },
        async fetchItemPrice(productId) {
            try {
                this.isLoadingPriceExternal = true;
                this.isItemAvailable = false;

                let result = await this.checkItemExternal(productId);

                let price = Number(result.price);
                this.unitCost = isNaN(price) ? 0 : price;
                this.isLoadingPriceExternal = false;
                this.isItemAvailable = true;
            } catch (error) {
                this.isLoadingPriceExternal = false;
                this._message(error.message, "danger");
                this.isItemAvailable = false;
            }
        }
    }
};
</script>

<style scoped>
.quantity {
    display: grid;
    grid-template-columns: 140px 1fr;
    align-items: center;
    column-gap: 10px;
}
</style>
